import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import FounderFrame from "@/components/FounderFrame";
import GearMark from "@/components/GearMark";
import { APPS, BOOKING_URL, CONTACT_EMAIL } from "@/config";
import usePageMeta from "@/hooks/usePageMeta";

const EXPECT = [
  `A live walkthrough of ${APPS[0].shortName} or ${APPS[1].shortName} on your use case`,
  "Straight answers on how Transactr fits your AWS motion",
  "Clear next steps — no obligation",
];

export default function Contact() {
  usePageMeta(
    "Contact — Transactr",
    "Book a demo or consultation directly with the founder — every walkthrough runs with Gary."
  );
  return (
    <section className="relative overflow-hidden" data-testid="contact-page">
      <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-60" />
      <div className="container-t relative pb-24 pt-36 lg:pb-32 lg:pt-44">
        <Reveal>
          <p className="eyebrow">
            <span className="eyebrow-mark" />
            Contact
          </p>
          <h1 className="mt-6 max-w-2xl font-display text-[clamp(2.4rem,4.6vw,3.8rem)] font-extrabold leading-[1.06] tracking-[-0.025em] text-ink">
            Book time with{" "}
            <em className="font-accent font-medium italic text-vermillion">Gary</em>.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-inksoft lg:text-lg">
            Every demo and consultation runs directly with the founder. Pick a time that works — the
            invitation lands in your calendar instantly.
          </p>
          <div className="mt-8 h-px w-24 bg-bluerule" />
        </Reveal>

        <div className="mt-14 grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal delay={0.1}>
            <FounderFrame />
            <div className="mt-10 pl-2">
              <p className="font-display text-xl font-bold text-ink">Gary</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft">
                Founder · Transactr
              </p>
              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT_EMAIL}`}
                className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-vermillion"
              >
                {CONTACT_EMAIL}
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="relative overflow-hidden rounded-[2rem] bg-coal p-8 text-white lg:p-12" data-testid="contact-booking-panel">
              <GearMark size={380} className="pointer-events-none absolute -right-24 -top-24 animate-spin-slower opacity-[0.08]" />
              <div className="relative">
                <p className="eyebrow text-bluerule">
                  <span className="eyebrow-mark" />
                  Schedule directly
                </p>
                <h2 className="mt-5 font-display text-3xl font-extrabold tracking-[-0.02em] lg:text-4xl">
                  Book a consultation
                </h2>
                <p className="mt-4 max-w-md leading-relaxed text-white/70">
                  Thirty seconds to pick a time; the meeting is yours. Bring one live channel or
                  alliance problem and we'll work it together.
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <a
                    data-testid="contact-book-consultation-btn"
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2.5 rounded-full bg-vermillion px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:bg-vermillion-deep hover:shadow-[0_18px_44px_-14px_rgba(214,53,10,0.7)] active:scale-[0.98]"
                  >
                    Book a consultation
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                  <a
                    data-testid="contact-get-a-demo-btn"
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:border-white/60 hover:bg-white/10 active:scale-[0.98]"
                  >
                    Get a demo
                    <ArrowRight size={16} />
                  </a>
                </div>
                <div className="mt-12 border-t border-white/15 pt-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
                    What to expect
                  </p>
                  <ul className="mt-5 space-y-3.5">
                    {EXPECT.map((e) => (
                      <li key={e} className="flex items-start gap-3 text-sm leading-relaxed text-white/80">
                        <Check size={15} className="mt-0.5 shrink-0 text-bluerule" />
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
